import React from 'react';
import {
  ambilSkorTerbaikPerModul,
  ambilRiwayatTryout,
  MODUL_LABEL,
} from './progressTracker';

// ─── Kartu Latihan Harian ─────────────────────────────────────
function KartuLatihanHarian() {
  const skorPerModul = ambilSkorTerbaikPerModul();
  const modulList = Object.keys(MODUL_LABEL);
  const sudahDikerjakan = modulList.filter(m => skorPerModul[m] !== undefined);
  const belumDikerjakan = modulList.filter(m => skorPerModul[m] === undefined);
  const belumAda = sudahDikerjakan.length === 0;

  if (belumAda) {
    return (
      <div className="rounded-xl p-5 text-center"
           style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
        <span className="text-3xl mb-2 block">🏋️</span>
        <p className="font-semibold text-sm mb-1" style={{ color: 'var(--text-primary)', fontFamily: "'Poppins', sans-serif" }}>
          LATIHAN HARIAN
        </p>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
          Kerjakan modul pertamamu untuk melihat progress latihan harian di sini.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl overflow-hidden"
         style={{ border: '1px solid var(--border)' }}>
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between"
           style={{ backgroundColor: 'var(--bg-card2)', borderBottom: '1px solid var(--border)' }}>
        <div className="flex items-center gap-2">
          <span>🏋️</span>
          <p className="font-bold text-sm" style={{ color: 'var(--text-primary)', fontFamily: "'Poppins', sans-serif" }}>
            LATIHAN HARIAN
          </p>
        </div>
        <p className="text-xs font-semibold" style={{ color: 'var(--accent)' }}>
          {sudahDikerjakan.length}/{modulList.length} modul
        </p>
      </div>

      {/* Modul yang sudah dikerjakan */}
      <div className="px-4 py-3 space-y-2.5"
           style={{ backgroundColor: 'var(--bg-card)' }}>
        {sudahDikerjakan.map(modulId => {
          const info = MODUL_LABEL[modulId];
          const skor = skorPerModul[modulId];
          return (
            <div key={modulId}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs flex items-center gap-1.5" style={{ color: 'var(--text-secondary)' }}>
                  <span>{info.icon}</span> {info.nama}
                </span>
                <span className="text-xs font-bold font-mono" style={{ color: 'var(--text-primary)' }}>
                  {skor}
                </span>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--bg-card3)' }}>
                <div className="h-full rounded-full transition-all"
                     style={{ width: `${skor}%`, backgroundColor: info.warna }} />
              </div>
            </div>
          );
        })}

        {/* Modul belum dikerjakan */}
        {belumDikerjakan.length > 0 && (
          <div className="pt-1">
            <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
              Belum dikerjakan
            </p>
            <div className="flex flex-wrap gap-1">
              {belumDikerjakan.map(modulId => (
                <span key={modulId} className="text-[10px] px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: 'var(--bg-card3)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
                  {MODUL_LABEL[modulId].icon} {MODUL_LABEL[modulId].nama}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Kartu Tryout Resmi ───────────────────────────────────────
function KartuTryout() {
  const riwayat = ambilRiwayatTryout();
  const belumAda = riwayat.length === 0;

  // Data tren untuk grafik
  const trenData = riwayat.slice(-6); // max 6 titik terakhir
  const W = 280, H = 80, PAD = 12;
  const skorList = trenData.map(d => d.skorAkumulasi);
  const maks = Math.max(100, ...skorList);

  const points = skorList.length >= 2
    ? skorList.map((s, i) => {
        const x = PAD + (i / (skorList.length - 1)) * (W - PAD * 2);
        const y = H - PAD - (s / maks) * (H - PAD * 2);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      }).join(' ')
    : null;

  const skorTerakhir = riwayat.length > 0 ? riwayat[riwayat.length - 1].skorAkumulasi : null;
  const skorSebelumnya = riwayat.length > 1 ? riwayat[riwayat.length - 2].skorAkumulasi : null;
  const trendNaik = skorTerakhir !== null && skorSebelumnya !== null && skorTerakhir >= skorSebelumnya;

  if (belumAda) {
    return (
      <div className="rounded-xl p-5 text-center"
           style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
        <span className="text-3xl mb-2 block">🏆</span>
        <p className="font-semibold text-sm mb-1" style={{ color: 'var(--text-primary)', fontFamily: "'Poppins', sans-serif" }}>
          TRYOUT RESMI
        </p>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
          Ikuti tryout resmi tiap 10 hari untuk melihat perkembangan skor akumulasimu di sini.
        </p>
        <div className="mt-3 px-3 py-2 rounded-lg text-xs"
             style={{ backgroundColor: 'var(--bg-card3)', color: 'var(--text-muted)' }}>
          ⏳ Tryout berikutnya akan segera tersedia
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl overflow-hidden"
         style={{ border: '1px solid var(--border)' }}>
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between"
           style={{ backgroundColor: 'var(--bg-card2)', borderBottom: '1px solid var(--border)' }}>
        <div className="flex items-center gap-2">
          <span>🏆</span>
          <p className="font-bold text-sm" style={{ color: 'var(--text-primary)', fontFamily: "'Poppins', sans-serif" }}>
            TRYOUT RESMI
          </p>
        </div>
        <p className="text-xs font-semibold" style={{ color: 'var(--accent)' }}>
          {riwayat.length} tryout
        </p>
      </div>

      <div className="px-4 py-3 space-y-3" style={{ backgroundColor: 'var(--bg-card)' }}>
        {/* Skor terakhir */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
              Skor Akumulasi Terakhir
            </p>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-extrabold font-mono" style={{ color: 'var(--accent)' }}>
                {skorTerakhir}
              </span>
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>/ 100</span>
              {skorSebelumnya !== null && (
                <span className="text-xs font-semibold" style={{ color: trendNaik ? 'var(--success)' : 'var(--danger)' }}>
                  {trendNaik ? '📈' : '📉'} {trendNaik ? '+' : ''}{skorTerakhir - skorSebelumnya}
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Tryout ke-</p>
            <p className="text-2xl font-bold font-mono" style={{ color: 'var(--text-primary)' }}>
              {riwayat[riwayat.length - 1].nomorTryout}
            </p>
          </div>
        </div>

        {/* Grafik tren */}
        {points && (
          <div>
            <p className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color: 'var(--text-muted)' }}>
              Tren {trenData.length} Tryout Terakhir
            </p>
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 80 }}>
              <polyline points={points} fill="none"
                        stroke="var(--accent)" strokeWidth="2.5"
                        strokeLinecap="round" strokeLinejoin="round"/>
              {skorList.map((s, i) => {
                const x = PAD + (i / (skorList.length - 1)) * (W - PAD * 2);
                const y = H - PAD - (s / maks) * (H - PAD * 2);
                return (
                  <g key={i}>
                    <circle cx={x.toFixed(1)} cy={y.toFixed(1)} r="4" fill="var(--accent)"/>
                    <text x={x.toFixed(1)} y={(y - 8).toFixed(1)} textAnchor="middle"
                          fontSize="9" fill="var(--text-muted)">{s}</text>
                  </g>
                );
              })}
            </svg>
          </div>
        )}

        {/* Riwayat singkat */}
        <div className="space-y-1.5">
          {[...riwayat].reverse().slice(0, 3).map((d, i) => {
            const tgl = new Date(d.tanggal);
            const tglStr = `${tgl.getDate()}/${tgl.getMonth()+1}/${tgl.getFullYear()}`;
            return (
              <div key={i} className="flex items-center justify-between text-xs py-1.5 px-2 rounded-lg"
                   style={{ backgroundColor: 'var(--bg-card2)' }}>
                <span style={{ color: 'var(--text-muted)' }}>Tryout #{d.nomorTryout} — {tglStr}</span>
                <span className="font-bold font-mono" style={{ color: 'var(--accent)' }}>{d.skorAkumulasi}</span>
              </div>
            );
          })}
        </div>

        <p className="text-[10px] text-center" style={{ color: 'var(--text-muted)' }}>
          💡 Kraepelin/Pauli berkontribusi paling besar pada skor akumulasi
        </p>
      </div>
    </div>
  );
}

// ─── Komponen Utama ───────────────────────────────────────────
export default function ProgressBeranda() {
  return (
    <div className="space-y-4">
      <KartuLatihanHarian />
      <KartuTryout />
    </div>
  );
}
